import { api } from '../../../services/api';
import type {
  Rule,
  RuleVersion,
  SimulationResult,
  RuleConflict,
  EventType,
} from '../types/rule.types';

const BASE = '/admin/template-rules';

export interface PropertyDefinitionResponse {
  id: string;
  label: string;
  group: string;
  type: string;
  picklistValues?: string[];
  description?: string;
}

export interface EventTypeResponse {
  id: EventType;
  label: string;
  description: string;
  category: string;
}

export const templateRulesService = {
  // Rules CRUD
  listForTenant: (tenantId: string) =>
    api.get<Rule[]>(`${BASE}/${tenantId}`),

  listForTemplate: (tenantId: string, templateId: string) =>
    api.get<Rule[]>(`${BASE}/${tenantId}/template/${templateId}`),

  getRule: (tenantId: string, ruleId: string) =>
    api.get<Rule>(`${BASE}/${tenantId}/${ruleId}`),

  createRule: (tenantId: string, data: {
    templateId: string;
    name: string;
    description: string;
    status: string;
    eventTypes: EventType[];
    priority: number;
    stopAfterMatch: boolean;
    conditionGroup: Rule['conditionGroup'];
  }) => api.post<Rule>(`${BASE}/${tenantId}`, data),

  updateRule: (tenantId: string, ruleId: string, data: {
    name?: string;
    description?: string;
    status?: string;
    eventTypes?: EventType[];
    priority?: number;
    stopAfterMatch?: boolean;
    conditionGroup?: Rule['conditionGroup'];
  }) => api.put<Rule>(`${BASE}/${tenantId}/${ruleId}`, data),

  deleteRule: (tenantId: string, ruleId: string) =>
    api.delete(`${BASE}/${tenantId}/${ruleId}`),

  // Versions
  listVersions: (tenantId: string, ruleId: string) =>
    api.get<RuleVersion[]>(`${BASE}/${tenantId}/${ruleId}/versions`),

  // Simulation
  simulate: (tenantId: string, ruleId: string, data: {
    testData: Record<string, unknown>;
    eventType: EventType;
  }) => api.post<SimulationResult>(`${BASE}/${tenantId}/${ruleId}/simulate`, data),

  // Conflict detection
  detectConflicts: (tenantId: string, data: {
    templateId: string;
    ruleIds?: string[];
  }) => api.post<RuleConflict[]>(`${BASE}/${tenantId}/detect-conflicts`, data),

  // Catalogs
  getEventTypes: (tenantId: string) =>
    api.get<EventTypeResponse[]>(`${BASE}/${tenantId}/catalog/event-types`),

  getProperties: (tenantId: string) =>
    api.get<PropertyDefinitionResponse[]>(`${BASE}/${tenantId}/catalog/properties`),
};
